import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FloppyDisk, X, Spinner, MicrosoftWordLogo, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import A4Sheet from "@/components/A4Sheet";

// Word-Download eines Schriftsatzes (Blob → Datei)
export async function downloadDocx(schriftsatz) {
  const r = await api.get(`/schriftsaetze/${schriftsatz.id}/docx`, { responseType: "blob" });
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/.exec(cd);
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = m?.[1] || `${schriftsatz.typ}.docx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function WordButton({ schriftsatz, size = "sm" }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button variant="outline" size={size} disabled={busy} data-testid={`schrift-docx-btn-${schriftsatz.typ}`}
      onClick={async () => { setBusy(true); try { await downloadDocx(schriftsatz); } catch (err) { toast.error(errMsg(err, "Word-Export fehlgeschlagen")); } finally { setBusy(false); } }}>
      {busy ? <Spinner size={14} className="mr-1 animate-spin" /> : <MicrosoftWordLogo size={14} className="mr-1" weight="duotone" />} Word (.docx)
    </Button>
  );
}

// Schriftsatz direkt in der Software bearbeiten — links Text, rechts Live-Vorschau auf A4
export default function SchriftsatzEditor({ schriftsatz, akte, user, onSaved, onCancel }) {
  const [text, setText] = useState(schriftsatz.content || "");
  const [saving, setSaving] = useState(false);
  const dirty = text !== (schriftsatz.content || "");

  useEffect(() => { setText(schriftsatz.content || ""); }, [schriftsatz.id, schriftsatz.content]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/schriftsaetze/${schriftsatz.id}`, { content: text });
      toast.success("Schriftsatz gespeichert");
      onSaved?.(r.data);
    } catch (err) { toast.error(errMsg(err, "Speichern fehlgeschlagen")); } finally { setSaving(false); }
  };

  const meta = {
    aktenzeichen: akte?.aktenzeichen,
    schadennr: akte?.gegner?.schadennr,
    empfaenger: akte?.gegner?.versicherung ? `${akte.gegner.versicherung}\n– Schadenabteilung –` : null,
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={`schriftsatz-editor-${schriftsatz.typ}`}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB] flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <PencilSimple size={16} weight="duotone" className="text-[#1A2238]" />
          <span className="font-medium">Bearbeiten</span>
          <span className="text-xs text-[#9CA3AF] hidden sm:inline">Absätze durch Leerzeile trennen · „Betreff:“ in den ersten Zeilen wird fett</span>
        </div>
        <div className="flex items-center gap-2">
          <WordButton schriftsatz={{ ...schriftsatz, content: text }} />
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="schrift-edit-cancel"><X size={14} className="mr-1" /> Schließen</Button>
          <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="schrift-edit-save">
            {saving ? <Spinner size={14} className="mr-1 animate-spin" /> : <FloppyDisk size={14} className="mr-1" />} Speichern
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 min-h-0">
        <div className="p-3 border-b xl:border-b-0 xl:border-r border-[#E5E7EB]">
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            className="min-h-[420px] xl:min-h-[900px] font-mono text-[13px] leading-relaxed resize-y"
            spellCheck
            data-testid="schrift-edit-textarea"
          />
        </div>
        <div className="p-3 bg-[#F3F4F6] overflow-auto max-h-[900px]">
          <A4Sheet content={text} user={user} typ={schriftsatz.typ} createdAt={schriftsatz.created_at} meta={meta} />
        </div>
      </div>
    </div>
  );
}
